import type { PlanDetailsCopy } from '../planDetails'

const de: PlanDetailsCopy = {
  fieldLabels: {
    current_age: 'Ihr Alter',
    retirement_age: 'Renteneintritt',
    annual_income: 'Jahreseinkommen',
    annual_savings: 'Jährliche Sparleistung',
    inflation: 'Inflationsrate',
    realization_tax_rate: 'Steuersatz auf Veräußerungsgewinne',
    cost_basis: 'Anschaffungskosten',
    w0: 'Anlagevermögen heute',
    primary_residence: 'Ihr Eigenheim',
    total_investable: 'Anlagevermögen',
    expected_return: 'Erwartete Rendite',
    volatility: 'Volatilität',
    horizon: 'Planungshorizont',
    jurisdiction: 'Steuerlicher Wohnsitz',
    risk_aversion: 'Risikotoleranz',
    birth_year: 'Geburtsjahr',
    birth_date: 'Geburtsdatum',
    marital_status: 'Familienstand',
    dependents: 'Unterhaltsberechtigte',
    dependent_ages: 'Alter der Unterhaltsberechtigten',
    children: 'Kinder',
    children_birth_years: 'Geburtsjahre der Kinder',
    partner_age: 'Alter Ihres Partners',
    partner_annual_income: 'Jahreseinkommen Ihres Partners',
    partner_retirement_age: 'Renteneintritt Ihres Partners',
    plan_to_age: 'Planungsende (Alter)',
    gross_annual_income: 'Bruttojahreseinkommen',
    net_income: 'Nettoeinkommen',
    annual_spending: 'Jährliche Ausgaben',
    essential_spending: 'Notwendige Ausgaben',
    flexible_spending: 'Flexible Ausgaben',
    investments_total: 'Summe der Anlagen',
    total_investable_wealth: 'Gesamtes Anlagevermögen',
    portfolio_mix: 'Portfolioaufteilung',
    cgt_rate: 'Steuersatz auf Kapitalerträge',
    interest_rate: 'Zinssatz',
    dividend_yield: 'Dividendenrendite',
    wealth_tax_rate: 'Vermögensteuersatz',
    filing_jurisdictions: 'Länder der Steuererklärung',
    citizenship: 'Staatsangehörigkeit',
    tax_residency: 'Angegebene steuerliche Ansässigkeit',
    state: 'Bundesstaat oder Provinz',
    region: 'Bundesland / Region',
    city: 'Stadt',
    locality: 'Gemeinde',
    church_tax_applicable: 'Kirchensteuerpflichtig',
    life_cover: 'Risikolebensversicherung',
    income_protection: 'Berufsunfähigkeitsschutz',
    employer_match: 'Arbeitgeberzuschuss',
    pension: 'Altersvorsorge',
    state_pension: 'Gesetzliche Rente',
    social_security: 'Sozialversicherung',
    wage_growth: 'Gehaltsentwicklung',
    stability: 'Einkommensstabilität',
    has_will: 'Testament vorhanden',
    risk_attitude: 'Risikoeinstellung',
  },
  keyLabels: {
    goal_priority: 'Rangfolge der Ziele',
    current_age: 'Alter',
    inflation: 'Inflationsannahme',
    jurisdiction: 'Steuerlicher Wohnsitz',
    realization_tax_rate: 'Steuer beim Verkauf zur Finanzierung eines Ziels',
    citizenship: 'Staatsangehörigkeit',
    total_investable: 'Anlagevermögen',
    annual_income: 'Jahreseinkommen',
    annual_savings: 'Jährliche Sparleistung',
    risk_tier: 'Risikostufe',
    retirement_age: 'Renteneintrittsalter',
    target_wealth: 'Zielvermögen',
    primary_residence_sellable: 'Verkauf des Eigenheims / Verkleinerung',
  },
  channelLabels: {
    web_levers: 'Stellhebel auf der Website',
    web_cockpit: 'Entscheidungs-Cockpit',
    web: 'Website',
    chat: 'KI-Assistent',
    mcp: 'KI-Assistent',
    full_review: 'Vollständige Prüfung',
    full_plan: 'Vollständiger Plan',
    what_if: 'Szenariorechnung',
  },
  objectiveLabels: {
    status_quo: 'Aktueller Stand',
    max_goal_probability: 'Höchste Erfolgswahrscheinlichkeit',
    lowest_drawdown: 'Geringster maximaler Verlust',
    highest_growth: 'Höchstes Wachstum',
    fastest_to_goal: 'Ziel am frühesten erreicht',
  },
  choices: {
    interest_only: 'Endfällig (nur Zinsen)',
    fixed_rate_amortizing: 'Annuitätendarlehen (fester Zins)',
    variable_rate_amortizing: 'Annuitätendarlehen (variabler Zins)',
    repayment: 'Tilgungsdarlehen',
    refinance: 'Anschlussfinanzierung zum Marktzins',
    repay: 'Vollständig tilgen',
  },
  values: {
    yearsShort: (n) => `${n} J.`,
    shareAbout: (pct) => `etwa ${pct} seines Werts`,
    sharePart: 'einen Teil seines Werts',
    aroundAge: (age) => ` etwa im Alter von ${age}`,
    inAboutYears: (n) => ` in etwa ${n} Jahren`,
    maySellFor: (share, when) => `Darf verkauft werden, um ${share}${when} freizusetzen – nur wenn das den Plan verbessert.`,
    maySell: 'Darf verkauft werden, wenn das den Plan verbessert.',
    structuredFact: 'Strukturierte Angabe',
  },
  ask: { talkThrough: 'Mit MyCIO besprechen' },
  review: {
    openInputCheckFailed: 'Die Prüfung offener Angaben konnte nicht abgeschlossen werden. Den Stand der Prüfung sehen Sie unten.',
    finishedLoading: 'Ihre Prüfung ist abgeschlossen. Ergebnisse werden geladen …',
    draftReady: 'Ihre vorläufige Prüfung liegt vor – unten finden Sie die abgeschlossene Analyse, was nicht berechnet werden konnte, und die Fragen, die den Rest ermöglichen würden.',
    finishedNotLoaded: 'Ihre Prüfung ist abgeschlossen, die Ergebnisse konnten aber noch nicht geladen werden. Neuer Versuch – die Berechnung wird nicht wiederholt.',
    fetchFailed: 'Ihre Prüfung ist abgeschlossen, konnte aber gerade nicht abgerufen werden. Sie ist gespeichert – versuchen Sie es gleich noch einmal; eine Neuberechnung ist nicht nötig.',
    notComputed: (list) => `In diesem Lauf nicht berechnet: ${list}.`,
    changesNotSaved: (n, f) => `${n === 1 ? '1 Änderung wurde nicht gespeichert' : `${f} Änderungen wurden nicht gespeichert`} – siehe Ihre Angaben.`,
  },
  facts: {
    chooseOption: 'Bitte wählen Sie eine Option.',
    couldNotSave: 'Der Wert konnte nicht gespeichert werden. Ihr Plan ist unverändert.',
    couldNotUndo: 'Die Änderung konnte nicht rückgängig gemacht werden.',
    choosePlaceholder: 'Auswählen …',
    confirmedBadge: 'Bestätigt – Änderung im Chat anfragen',
    confirmedTitle: (was) => `Sie haben diesen Wert bereits als ${was} bestätigt. Bitten Sie MyCIO im Chat, ihn zu ersetzen.`,
  },
  assumptionsToConfirm: { anInput: 'eine Angabe', assumedSuffix: (v) => ` – angenommen: ${v}` },
  editor: {
    enterNumber: 'Bitte geben Sie eine Zahl ein.',
    enterNumberFor: (l) => `Bitte geben Sie eine Zahl für ${l} ein.`,
    enterPercentFor: (l) => `Bitte geben Sie einen Prozentsatz für ${l} ein.`,
    chooseYesNoFor: (l) => `Bitte wählen Sie Ja oder Nein für ${l}.`,
    semicolonBetween: (l) => `Trennen Sie die Einträge von ${l} mit „;“.`,
    useNumbersFor: (l) => `Bitte verwenden Sie Zahlen für ${l}.`,
    wholeNumbersFor: (l) => `Bitte verwenden Sie ganze Zahlen für ${l}, getrennt durch „;“.`,
    commaSeparated: 'durch Kommas getrennt',
  },
  freshnessUnknown: 'Wir konnten nicht prüfen, ob Ihre gespeicherte Berechnung die neuesten Kontoänderungen enthält. Laden Sie die Seite neu und prüfen Sie Ihre Konten, bevor Sie sich auf diese Zahlen stützen.',
  goals: { goalN: (n) => `Ziel ${n}`, whatMoves: 'was es bewegt →', earliest: (y) => `frühestens ≈ ${y}`, rank: 'Rang' },
  issues: {
    mainConstraint: 'Die wichtigste Einschränkung dieses Plans',
    riskToReview: 'Zu prüfendes Risiko',
    missingInformation: 'Fehlende Angaben',
    whyItMatters: (t) => `Warum es wichtig ist: ${t}`,
    seeEvidence: 'Belege ansehen ↓',
    noneFound: 'Die abgeschlossene Analyse hat mit den derzeit in diesem Plan enthaltenen Angaben keinen wesentlichen Befund ergeben.',
    notAvailable: 'Die Befundanalyse liegt noch nicht vor. Ein leerer Abschnitt bedeutet nicht, dass es keine Befunde gibt.',
    discussQuestion: 'Gehen Sie die Befunde dieses Plans durch. Erklären Sie, worauf sie beruhen, und fragen Sie mich, ob fehlende Angaben zu meiner Situation ihre Priorität oder ihr Ergebnis ändern könnten.',
    discussLabel: 'Diese Befunde besprechen',
  },
  policies: {
    optionTested: 'Geprüfte Option',
    computedEffect: (v) => `Berechnete Wirkung: ${v}`,
    tradeoff: (v) => `Kompromiss: ${v}`,
    backToAction: 'Zurück zur Maßnahme ↑',
    tradeoffsHeading: 'Kompromisse aus den geprüften Fällen',
  },
  transfer: {
    toHeirs: (m) => `+${m} für die Erben`,
    fundedPoints: (label, pts) => `${label} ${pts} Prozentpunkte`,
    noChange: ' · keine Änderung bei der Finanzierung Ihrer Ziele',
  },
  tax: {
    rulesIntro: 'Es werden nur Regeln angezeigt, die dieser Berechnung zugeordnet sind. Jeder Eintrag zeigt die erfassten Quellenangaben; fehlende Daten oder Angaben zum Geltungsbereich bleiben als fehlend gekennzeichnet.',
    title: 'In diesem Plan verwendete steuerliche Behandlung',
    countryBookPrimary: 'Die Projektion hat Jahr für Jahr das jeweilige nationale Steuerrecht angewandt. Quellen, die ausdrücklich als amtlich oder primär erfasst sind, sind unten aufgeführt.',
    countryBookNoPrimary: 'Die Projektion hat Jahr für Jahr das jeweilige nationale Steuerrecht angewandt. Diesem gespeicherten Ergebnis sind noch keine Primärquellen beigefügt; prüfen Sie steuerlich relevante Entscheidungen daher, bevor Sie handeln.',
    simplified: 'Die Berechnung hat eine vereinfachte steuerliche Behandlung verwendet. Prüfen Sie die Einschränkungen unten, bevor Sie sich auf steuerlich relevante Vergleiche stützen.',
    askQuestion: 'Erklären Sie, welche Steuergesetze und nationalen Ebenen auf diesen Plan angewandt wurden, welche angenähert oder ausgeklammert wurden und welche Angaben zu meiner steuerlichen Situation das Ergebnis ändern könnten. Nutzen Sie nur Primärquellen, die dieser Berechnung tatsächlich beigefügt sind, und nennen Sie fehlende Quellen.',
    askLabel: 'Zur steuerlichen Behandlung fragen',
  },
  inputs: { title: 'Angaben, die das Ergebnis ändern könnten', infoToConfirm: 'Zu bestätigende Angaben' },
  gated: {
    countChoices: (n, f) => (n === 1 ? `${f} für Sie ausgearbeitete Option` : `${f} für Sie ausgearbeitete Optionen`),
    mappedYears: (_n, f) => `wir haben die nächsten ${f} Jahre durchgerechnet`,
    extraChecks: (n, f) => (n === 1 ? `${f} zusätzliche Prüfung Ihres Plans` : `${f} zusätzliche Prüfungen Ihres Plans`),
    placeholderRows: ['Beispielzeile', 'Beispielwert', 'Beispieloption', 'Platzhalter'],
  },
}

export default de
